import { MutationHookOptions, MutationTuple, ObservableQuery, useApolloClient, useMutation } from '@apollo/client';

import {
  CreateRdProjectMutation,
  CreateRdProjectMutationVariables,
  CREATE_RD_PROJECT,
  GET_RD_PROJECTS,
  GetRdProjectsQueryVariables,
  getOperationName,
} from '@neo.tax/shared';

import { useRdContext } from '../context';

type UseCreateRdProject = MutationTuple<CreateRdProjectMutation, CreateRdProjectMutationVariables>;

export const useCreateRdProject = (
  options?: MutationHookOptions<CreateRdProjectMutation, CreateRdProjectMutationVariables>,
): UseCreateRdProject => {
  const { fiscalYearId } = useRdContext();
  const client = useApolloClient();

  return useMutation<CreateRdProjectMutation, CreateRdProjectMutationVariables>(CREATE_RD_PROJECT, {
    ...options,
    context: {
      ...(options?.context ?? {}),
      successToast: { title: 'Project created successfully' },
      errorToast: {
        title: 'Error creating project',
        description: 'Something unexpected prevented your changes from saving. Please try again.',
      },
    },
    refetchQueries: () => {
      const map = client.getObservableQueries() as Map<string, ObservableQuery<any, any>>;

      const targets = Array.from(map.values())
        .filter((oq) => {
          const name = getOperationName(oq.query);
          const isGetRdProjects = name === 'GetRdProjects' || oq.query === GET_RD_PROJECTS;
          if (!isGetRdProjects) return false;
          const vars = oq.variables as GetRdProjectsQueryVariables | undefined;
          return vars?.fiscalYearId === fiscalYearId;
        })
        .map((oq) => ({ query: GET_RD_PROJECTS, variables: oq.variables }));

      return targets;
    },
    awaitRefetchQueries: true,
  });
};
